(() => {
  const selector = '[data-slideshow-one-progress]';

  // A section may include this asset again during an editor reload.
  if (window.tclSlideshowOneProgress) {
    window.tclSlideshowOneProgress.init(document);
    return;
  }

  class SlideshowOneProgress {
    constructor(container) {
      this.container = container;
      this.duration = Number(container.dataset.progressDuration) || 5000;
      this.autoplay = container.dataset.progressAutoplay === 'true';
      const controls = container.querySelector('.slideshow__control-wrapper');
      this.dots = controls ? Array.from(controls.querySelectorAll('.slider-counter__link')) : [];
      this.activeIndex = -1;
      this.bars = this.dots.map((dot) => {
        let bar = dot.querySelector('.slideshow__progress-bar');
        if (!bar) {
          bar = document.createElement('div');
          bar.className = 'slideshow__progress-bar';
          dot.appendChild(bar);
        }
        bar.style.transform = 'scaleX(0)';
        bar.style.transition = `transform ${this.duration}ms linear`;
        bar.style.willChange = 'transform';
        return bar;
      });
      if (!this.dots.length) return;

      this.dotObserver = new MutationObserver(() => this.syncActiveDot());
      this.dots.forEach((dot) => {
        this.dotObserver.observe(dot, { attributes: true, attributeFilter: ['class', 'aria-current'] });
      });

      const autoplayButton = container.querySelector('.slideshow__autoplay');
      if (autoplayButton) {
        this.autoplay = !autoplayButton.classList.contains('slideshow__autoplay--paused');
        this.autoplayObserver = new MutationObserver(() => {
          this.autoplay = !autoplayButton.classList.contains('slideshow__autoplay--paused');
          if (!this.autoplay) this.resetAll();
          else if (this.activeIndex >= 0) this.startForIndex(this.activeIndex);
        });
        this.autoplayObserver.observe(autoplayButton, { attributes: true, attributeFilter: ['class'] });
      }
      this.initialTimer = window.setTimeout(() => this.syncActiveDot(), 50);
    }

    clearTimer() {
      window.clearTimeout(this.timer);
      this.timer = null;
      window.cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    resetAll(noTransition = false) {
      this.clearTimer();
      this.bars.forEach((bar) => {
        if (noTransition) bar.style.transition = 'none';
        bar.style.transform = 'scaleX(0)';
      });
      if (noTransition && this.bars.length) {
        // Flush all reset writes together, rather than once per dot.
        void this.container.offsetWidth;
        this.bars.forEach((bar) => {
          bar.style.transition = `transform ${this.duration}ms linear`;
        });
      }
    }

    startForIndex(index) {
      if (!this.autoplay) return;
      this.resetAll(true);
      const bar = this.bars[index];
      if (!bar) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        if (!this.container.isConnected || !this.autoplay || this.activeIndex !== index) return;
        bar.style.transform = 'scaleX(1)';
      });
      this.timer = window.setTimeout(() => {
        const dot = this.dots[index];
        if (!dot.classList.contains('slider-counter__link--active') || dot.getAttribute('aria-current') !== 'true') {
          this.resetAll();
        }
      }, this.duration + 20);
    }

    syncActiveDot() {
      const index = this.dots.findIndex((dot) =>
        dot.classList.contains('slider-counter__link--active') && dot.getAttribute('aria-current') === 'true'
      );
      if (index === this.activeIndex) return;
      this.activeIndex = index;
      if (index < 0) this.resetAll();
      else this.startForIndex(index);
    }

    destroy() {
      window.clearTimeout(this.initialTimer);
      this.dotObserver?.disconnect();
      this.autoplayObserver?.disconnect();
      this.resetAll();
      this.bars.forEach((bar) => { bar.style.removeProperty('will-change'); });
    }
  }

  const instances = new Map();
  const init = (root) => {
    const containers = Array.from(root.querySelectorAll(selector));
    if (root.matches?.(selector)) containers.unshift(root);
    containers.forEach((container) => {
      if (!instances.has(container)) instances.set(container, new SlideshowOneProgress(container));
    });
  };
  window.tclSlideshowOneProgress = { init };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else init(document);

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((instance, container) => {
      if (event.target === container || event.target.contains(container)) {
        instance.destroy();
        instances.delete(container);
      }
    });
  });
})();
